/**
 * Hook for ProjectResponseImageChoice component logic
 * Handles value selection and click handling
 *
 * `tentativeValue` is a visual-only hint (e.g. from a live voice transcript).
 * It renders as selected but is ignored by `handleClick` so it never mutates
 * committed form state. Any real `value` takes precedence.
 */
export function useImageChoice(
  value: string,
  onClick: (value: string) => void,
  maxSelections: number | undefined,
  readonly: boolean | undefined,
  tentativeValue?: string
) {
  const highlightedValue = value || tentativeValue || '';
  const isValueSelected = (selectedValue: string) =>
    highlightedValue.split(',').includes(selectedValue);
  const isValueCommitted = (selectedValue: string) =>
    value.split(',').includes(selectedValue);

  const handleClick = (clickedValue: string) => {
    if (readonly) return;

    const currentValues = value ? value.split(',').filter(Boolean) : [];

    if (isValueCommitted(clickedValue)) {
      onClick(currentValues.filter((v) => v !== clickedValue).join(','));
    } else {
      // If we've reached the maximum selection limit, replace the last selection
      if (maxSelections && currentValues.length >= maxSelections) {
        const newSelections = [...currentValues.slice(0, -1), clickedValue];
        onClick(newSelections.join(','));
        return;
      }

      onClick([...currentValues, clickedValue].join(','));
    }
  };

  return {
    isValueSelected,
    handleClick,
  };
}
